import { matchPath } from 'react-router-dom';
import type { PermissionAction } from '@troia-v3/types';

/**
 * Quem pode abrir cada tela — FONTE ÚNICA (20/10/2026).
 *
 * A trava das rotas (`RouteAccessGuard`), o menu lateral, os atalhos do topo
 * da sidebar, a barra do celular, a busca ⌘K e o redirecionamento da home
 * consultam esta tabela. Antes cada superfície tinha a sua lista e elas
 * divergiam: o Chat aparecia nos atalhos para quem não tinha `chat:read`,
 * "Integração" exigia `company:read` em vez de `api-keys:read`, e nenhuma rota
 * tinha trava — qualquer tela abria digitando o endereço.
 *
 * Regras da tabela:
 * - Toda rota de `router.tsx` dentro do layout precisa de uma entrada aqui. O
 *   teste `route-access.test.ts` lê o router e reprova rota sem entrada.
 * - Endereço sem entrada é NEGADO (`canAccessPath` devolve false). Assim um
 *   item de menu que aponta para tela inexistente some sozinho, e uma rota
 *   nova esquecida aparece como "acesso negado" em vez de vazar.
 * - A permissão espelha a do servidor (rota principal da tela). Tela de
 *   criação exige `create`; tela de detalhe/edição exige `read`, porque várias
 *   servem de visualização para quem só lê.
 * - `{ public: true }` = qualquer pessoa logada (conta pessoal, notificações,
 *   caixa de entrada de checklists, redirecionamentos — o destino é travado).
 */

export type AccessRule =
  | { module: string; action?: PermissionAction }
  | { anyOf: AccessRule[] }
  | { public: true };

/** Formato real de `state.user.permissions` (permissões do nível). */
export type ModulePermissions = Record<string, { actions?: string[]; scope?: string } | undefined>;

const PUBLIC: AccessRule = { public: true };
const read = (module: string): AccessRule => ({ module, action: 'read' });
const create = (module: string): AccessRule => ({ module, action: 'create' });

/**
 * Padrões no formato do react-router (com `/` inicial). A ordem só importa
 * para legibilidade: cada padrão casa o endereço inteiro (`end: true`).
 */
export const ROUTE_ACCESS: ReadonlyArray<{ path: string; rule: AccessRule }> = [
  { path: '/', rule: PUBLIC },

  // Dashboards
  { path: '/dashboard/management/support', rule: read('dashboards-support') },
  { path: '/dashboard/management/commercial', rule: read('dashboards-commercial') },
  { path: '/dashboard/management/marketing', rule: read('dashboards-marketing') },
  { path: '/dashboard/management/analytics', rule: read('dashboards-analytics') },
  { path: '/dashboard/management/sales', rule: read('crm') },

  // CRM
  { path: '/crm', rule: read('crm') },
  { path: '/crm/seller', rule: PUBLIC }, // redireciona para /dashboard/management/sales
  { path: '/crm/leads', rule: read('crm') },
  { path: '/crm/customers', rule: read('customers') },
  { path: '/crm/customers/new', rule: create('customers') },
  { path: '/crm/customers/:id', rule: read('customers') },
  { path: '/crm/customers/:id/edit', rule: read('customers') },
  { path: '/crm/ranking', rule: read('crm') },
  { path: '/crm/:funnelId', rule: read('crm') },

  // Agenda
  { path: '/calendar', rule: read('calendar') },

  // Atendimento — o e-mail é uma caixa de conversas (o servidor exige chat:read)
  { path: '/chat', rule: read('chat') },
  { path: '/chat/:conversationId', rule: read('chat') },
  { path: '/chat/management', rule: read('chat') },
  { path: '/history', rule: read('chat') },
  { path: '/history/:contactId/:channelId', rule: read('chat') },
  { path: '/email', rule: read('chat') },
  { path: '/email/:conversationId', rule: read('chat') },

  // Cobrança — mesma regra do item no menu de conta
  { path: '/billing', rule: read('company') },
  { path: '/billing/plans', rule: read('company') },

  // Pessoais
  { path: '/notifications', rule: PUBLIC },
  { path: '/settings', rule: PUBLIC },
  { path: '/settings/account', rule: PUBLIC },
  { path: '/settings/notifications', rule: PUBLIC },
  { path: '/settings/security', rule: PUBLIC },
  { path: '/settings/mcp', rule: PUBLIC },
  { path: '/settings/customization', rule: PUBLIC }, // redireciona para /settings

  // Configurações
  { path: '/settings/channels', rule: read('channels') },
  { path: '/settings/channels/:id/edit', rule: read('channels') },
  { path: '/settings/channels/:id/dashboard', rule: read('channels') },
  { path: '/settings/users', rule: read('users') },
  { path: '/settings/users/create', rule: create('users') },
  { path: '/settings/users/:id/edit', rule: read('users') },
  { path: '/settings/levels', rule: read('levels') },
  { path: '/settings/levels/new', rule: create('levels') },
  { path: '/settings/levels/:id/edit', rule: read('levels') },
  { path: '/settings/goals', rule: read('goals') },
  { path: '/settings/gamification', rule: read('gamification') },
  { path: '/settings/teams', rule: read('teams') },
  { path: '/settings/teams/new', rule: create('teams') },
  { path: '/settings/teams/:id/edit', rule: read('teams') },
  { path: '/settings/shifts', rule: read('shifts') },
  { path: '/settings/shifts/new', rule: create('shifts') },
  { path: '/settings/shifts/:id/edit', rule: read('shifts') },
  { path: '/settings/sla-policies', rule: read('sla-policies') },
  { path: '/settings/units', rule: read('units') },
  { path: '/units', rule: PUBLIC }, // redireciona para /settings/units
  { path: '/settings/tags', rule: read('tags') },
  { path: '/settings/email-domains', rule: read('email-domains') },
  { path: '/settings/email-domains/:id', rule: read('email-domains') },

  // Integração (chaves de API)
  { path: '/integration/api-keys', rule: read('api-keys') },

  // Prospecção
  { path: '/prospecting/templates', rule: read('templates') },
  { path: '/prospecting/templates/new', rule: create('templates') },
  { path: '/prospecting/templates/:id/edit', rule: read('templates') },
  { path: '/prospecting/audiences', rule: read('audiences') },
  { path: '/prospecting/audiences/:id', rule: read('audiences') },
  { path: '/prospecting/campaigns', rule: read('campaigns') },
  { path: '/prospecting/campaigns/new', rule: create('campaigns') },
  { path: '/prospecting/campaigns/:id', rule: read('campaigns') },
  { path: '/prospecting/campaigns/:id/edit', rule: read('campaigns') },

  // Automação — `/automation` redireciona para Workflows
  { path: '/automation', rule: read('workflows') },
  { path: '/automation/workflows', rule: read('workflows') },
  { path: '/automation/workflows/folder/:folderId', rule: read('workflows') },
  { path: '/automation/workflows/new', rule: create('workflows') },
  { path: '/automation/workflows/:id', rule: read('workflows') },
  // Skills são workflows (a tela lê /workflows)
  { path: '/automation/skills', rule: read('workflows') },
  { path: '/automation/skills/new', rule: create('workflows') },
  { path: '/automation/skills/:id', rule: read('workflows') },
  { path: '/automation/ai-agents', rule: read('agents') },
  { path: '/automation/ai-agents/new', rule: create('agents') },
  { path: '/automation/ai-agents/:id/edit', rule: read('agents') },
  { path: '/automation/custom-actions', rule: read('tools') },
  { path: '/automation/custom-actions/new', rule: create('tools') },
  { path: '/automation/custom-actions/:id/edit', rule: read('tools') },
  { path: '/automation/voices', rule: read('voices') },
  { path: '/automation/voices/new', rule: create('voices') },
  { path: '/automation/voices/:id/edit', rule: read('voices') },
  { path: '/automation/forms', rule: read('forms') },
  { path: '/automation/forms/new', rule: create('forms') },
  { path: '/automation/forms/:id/edit', rule: read('forms') },
  { path: '/automation/forms/:id/report', rule: read('forms') },

  // Checklists. "Meus checklists", preencher e abrir um checklist são da
  // pessoa designada/seguidora — o servidor confere caso a caso.
  { path: '/checklists', rule: read('checklists') },
  { path: '/checklists/my', rule: PUBLIC },
  { path: '/checklists/templates', rule: read('checklists') },
  { path: '/checklists/templates/new', rule: create('checklists') },
  { path: '/checklists/templates/:id/edit', rule: read('checklists') },
  { path: '/checklists/:id/fill', rule: PUBLIC },
  { path: '/checklists/:id', rule: PUBLIC },

  // Bases de dados
  { path: '/databases', rule: read('databases') },
  { path: '/databases/:id', rule: read('databases') },
  { path: '/databases/:id/documents/new', rule: create('databases') },
  { path: '/databases/:id/documents/:documentId', rule: read('databases') },
  { path: '/databases/:id/documents/:documentId/edit', rule: read('databases') },

  // Websites
  { path: '/websites', rule: read('websites') },
  { path: '/websites/:id', rule: read('websites') },

  // Help Desk
  { path: '/helpdesk/tickets', rule: read('tickets') },
  { path: '/helpdesk/tickets/retention', rule: read('tickets') },
  { path: '/helpdesk/tickets/:id', rule: read('tickets') },
  { path: '/helpdesk/projects', rule: read('projects') },
  { path: '/helpdesk/projects/new', rule: create('projects') },
  { path: '/helpdesk/projects/:id', rule: read('projects') },

  // Relatórios
  { path: '/reports/crm', rule: read('reports') },
  { path: '/reports/csat', rule: read('reports-csat') },
];

export function hasModuleAction(
  permissions: ModulePermissions | undefined,
  module: string,
  action: string = 'read',
): boolean {
  const actions = permissions?.[module]?.actions;
  return Array.isArray(actions) && actions.includes(action);
}

export function evaluateAccessRule(permissions: ModulePermissions | undefined, rule: AccessRule): boolean {
  if ('public' in rule) return true;
  if ('anyOf' in rule) return rule.anyOf.some((r) => evaluateAccessRule(permissions, r));
  return hasModuleAction(permissions, rule.module, rule.action ?? 'read');
}

/** Regra da rota que casa o endereço (sem query/hash), ou `undefined`. */
export function findRouteAccessRule(href: string): AccessRule | undefined {
  const pathname = href.split(/[?#]/)[0] || '/';
  // Específica antes de genérica: `/crm/leads` também casa `/crm/:funnelId`,
  // e `/helpdesk/tickets/retention` também casa `/helpdesk/tickets/:id`.
  const matches = ROUTE_ACCESS.filter((entry) => matchPath({ path: entry.path, end: true }, pathname));
  if (matches.length === 0) return undefined;
  const staticSegments = (path: string) => path.split('/').filter((s) => s && !s.startsWith(':')).length;
  return matches.reduce((best, cur) => (staticSegments(cur.path) > staticSegments(best.path) ? cur : best)).rule;
}

/** Endereço sem regra é negado — ver comentário do topo. */
export function canAccessPath(permissions: ModulePermissions | undefined, href: string): boolean {
  const rule = findRouteAccessRule(href);
  return rule ? evaluateAccessRule(permissions, rule) : false;
}

/**
 * Para onde mandar quem não pode abrir a tela inicial do próprio nível: a
 * primeira desta lista que a pessoa pode abrir (mesma ordem do menu lateral).
 * O último item é público, então sempre há destino.
 */
export const HOME_FALLBACK_PATHS: readonly string[] = [
  '/dashboard/management/support',
  '/dashboard/management/commercial',
  '/dashboard/management/marketing',
  '/dashboard/management/analytics',
  '/dashboard/management/sales',
  '/chat/management',
  '/chat',
  '/calendar',
  '/crm',
  '/crm/customers',
  '/helpdesk/tickets',
  '/helpdesk/projects',
  '/checklists',
  '/automation/ai-agents',
  '/automation/workflows',
  '/automation/forms',
  '/prospecting/campaigns',
  '/prospecting/templates',
  '/prospecting/audiences',
  '/reports/crm',
  '/reports/csat',
  '/databases',
  '/websites',
  '/settings/users',
  '/settings/channels',
  '/settings/account',
];

export function firstAccessiblePath(permissions: ModulePermissions | undefined, paths: readonly string[]): string | undefined {
  return paths.find((p) => canAccessPath(permissions, p));
}
